import { Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';

import Modal from '../../../components/general/modal';
import { SnackbarTypeSeverity } from '../../../constants/types';
import { completeManualRequestService } from '../../../services/request';
import { canManuallyCompleteRequest } from './requestModification';
import { IRequest, ServiceType } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface ManualCompleteActionCoreProps {
    request: IRequest;
    requestServiceType: ServiceType;
    permissions: string[];
    loading: boolean;
    onConfirm: () => void | Promise<boolean>;
}

// Presentational core: shows the "mark as performed" (Acte effectué) button + a
// confirmation modal when the policy allows it (canManuallyCompleteRequest),
// otherwise renders nothing.
export const ManualCompleteActionCore: React.FC<ManualCompleteActionCoreProps> = ({ request, requestServiceType, permissions, loading, onConfirm }) => {
    const [open, setOpen] = useState(false);

    if (!canManuallyCompleteRequest(request, requestServiceType, permissions)) {
        return null;
    }

    // Modal.confirmAction does not auto-close; close only on success so a failed
    // completion (e.g. the API rejecting) leaves the modal open. onConfirm resolves
    // useRequestMutation.run's boolean — that's what makes the close conditional.
    const handleConfirm = async () => {
        if (await onConfirm()) {
            setOpen(false);
        }
    };

    return (
        <>
            <Modal
                open={open}
                closeModal={() => setOpen(false)}
                confirmAction={handleConfirm}
                disabled={loading}
                title={<Translate id="pages.hospital.services.request.complete.title" />}
            >
                <Translate id="pages.hospital.services.request.complete.modal" />
            </Modal>
            {/* SHER-1062: the "Prochaine action" of the request-management panel — the one
                filled button in it, because it is the single step the workflow offers here. */}
            <Button variant="contained" color="success" startIcon={<CheckIcon />} onClick={() => setOpen(true)} data-testid="complete-request">
                <Translate id="pages.hospital.services.request.complete.button" />
            </Button>
        </>
    );
};

interface ManualCompleteActionProps {
    request: IRequest;
    requestServiceType: ServiceType;
    uuidInvestigation: string;
    permissions: string[];
    onCompleted: (updated: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// Container: wires the manual-completion service to the presentational core.
const ManualCompleteAction: React.FC<ManualCompleteActionProps> = ({ request, requestServiceType, uuidInvestigation, permissions, onCompleted, setShowSnackbar }) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onCompleted);

    const handleConfirm = () =>
        run(() => completeManualRequestService(uuidInvestigation, request.id), 'pages.hospital.services.request.complete.success');

    return (
        <ManualCompleteActionCore
            request={request}
            requestServiceType={requestServiceType}
            permissions={permissions}
            loading={loading}
            onConfirm={handleConfirm}
        />
    );
};

export default ManualCompleteAction;
